import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { imageEditingActions } from '../../reducers/imageEditing';
import Modal from '../common/Modal';
import Button from '../common/Button';

interface TemplateSaveModalProps {
  show: boolean;
  onHide: () => void;
}

const TemplateSaveModal: React.FC<TemplateSaveModalProps> = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [templateName, setTemplateName] = useState('');
  const [nameError, setNameError] = useState('');

  const { features, templateLoading, templates } = useSelector((state: RootState) => state.imageEditing);

  // モーダルが開かれたときに名前をクリア
  useEffect(() => {
    if (show) {
      setTemplateName('');
      setNameError('');
    }
  }, [show]);

  // 現在の設定を取得
  const getCurrentSettings = () => {
    const settings: any = {
      compression: features.compression.enabled ? features.compression.settings : null,
      watermark: features.watermark.enabled ? features.watermark.settings : null,
    };

    // 個別処理モードの設定
    if (features.contrastAdjustment?.enabled) {
      settings.contrastAdjustment = features.contrastAdjustment.settings;
    }
    if (features.brightnessAdjustment?.enabled) {
      settings.brightnessAdjustment = features.brightnessAdjustment.settings;
    }
    if (features.whiteBalance?.enabled) {
      settings.whiteBalance = features.whiteBalance.settings;
    }

    return settings;
  };

  // 名前のバリデーション
  const validateName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError('テンプレート名を入力してください');
      return false;
    }
    if (name.trim().length > 20) {
      setNameError('テンプレート名は20文字以内で入力してください');
      return false;
    }
    // 重複チェック
    if (templates.some(t => t.name === name.trim())) {
      setNameError('同じ名前のテンプレートが既に存在します');
      return false;
    }
    setNameError('');
    return true;
  };

  // 保存処理
  const handleSave = async () => {
    if (!validateName(templateName)) {
      return;
    }

    const settings = getCurrentSettings();

    await dispatch(imageEditingActions.saveTemplate({
      name: templateName.trim(),
      settings
    }) as any);

    onHide();
  };

  // 文字数カウント
  const charCount = templateName.length;
  const charCountColor = charCount > 20 ? 'text-red-500' : charCount > 15 ? 'text-yellow-500' : 'text-gray-500';

  const actions = (
    <>
      <Button
        onClick={handleSave}
        variant="primary"
        disabled={templateLoading || !templateName.trim() || !!nameError}
      >
        {templateLoading ? '保存中...' : '保存'}
      </Button>
      <Button variant="secondary" onClick={onHide} disabled={templateLoading}>
        キャンセル
      </Button>
    </>
  );

  return (
    <Modal
      show={show}
      onClose={onHide}
      title="テンプレートを保存"
      actions={actions}
    >
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          テンプレート名
        </label>
        <input
          type="text"
          className={`w-full px-3 py-2 border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
          placeholder="テンプレート名を入力（最大20文字）"
          value={templateName}
          onChange={(e) => {
            setTemplateName(e.target.value);
            validateName(e.target.value);
          }}
          maxLength={25}
        />
        <div className="flex justify-between mt-1">
          {nameError && (
            <span className="text-sm text-red-600 font-medium">{nameError}</span>
          )}
          <span className={`text-sm ml-auto ${charCountColor}`}>
            {charCount}/20
          </span>
        </div>
      </div>

      <div className="mt-4 p-3 bg-gray-50 rounded-md">
        <p className="text-sm text-gray-600 mb-2">保存される設定:</p>
        <ul className="text-sm text-gray-700 space-y-1">
          {features.compression.enabled && <li>• 圧縮設定</li>}
          {features.watermark.enabled && <li>• ウォーターマーク設定</li>}
          {features.contrastAdjustment?.enabled && <li>• コントラスト調整</li>}
          {features.brightnessAdjustment?.enabled && <li>• 明るさ調整</li>}
          {features.whiteBalance?.enabled && <li>• ホワイトバランス</li>}
        </ul>
      </div>
    </Modal>
  );
};

export default TemplateSaveModal;